import { useAppTheme } from "../../hooks/useAppTheme";
import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import { ScrollView, StyleSheet, Text, TouchableOpacity, Modal as RNModal, Pressable, Platform, View as RNView } from "react-native";
import { View } from "../../components/shared/Themed";
import { LabelBannerWithSearchBar } from "../../components/custom/Banners/LabelBannerWithSearchBar";
import { DataTableWithPagination } from "../../components/custom/DataTable/DataTableWithPagination";
import Spinner from "../../components/shared/Spinner";
import { getQldProductionStocks } from "../../services/stockService";
import DeviceOrientationContext from "../../components/context/DeviceOrientationContext";
import {
  headers,
  MediumDeviceHeaders,
  SmallDeviceHeaders,
} from "./utils/productionStockHeaders";
import { productionStockModel } from "../../models/reportsModel";
import { useSelector } from "react-redux";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { translateHeaders, useTranslation } from "../../utils/useTranslation";
import { productionStockTranslationMap } from "../../utils/translations";
// @ts-ignore
import { Feather } from "@expo/vector-icons";
import { handleTableLoader } from "../../utils/common";
import { sortColumns } from "../../utils/sortColumns";
import { filterBasedOnAnyKey } from "../../utils/searchInArrays";

export default function ProductionStocksReportScreen({
  route,
  navigation,
}: any) {
  const { colors } = useAppTheme();
  const { isLargeDevice, isMediumDevice } = useContext(
    DeviceOrientationContext
  );
  const { t } = useTranslation();
  const { productionStockReport, isProductionStockReportListLoaded } =
    useSelector((state: any) => state.reports);
  const { language } = useSelector((state: any) => state.configurations);

  const [selected, setSelected] = useState<productionStockModel>(
    {} as unknown as productionStockModel
  );

  const [prodStockPageLevelList, setProdStockPageLevelList] = useState<
    Array<productionStockModel>
  >([]);
  const [isSearched, setIsSearched] = useState<boolean>(false);

  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      headers
    );
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [isRefresh, setRefresh] = useState<boolean>(false);

  // Column visibility settings
  const [columnSettingsOpen, setColumnSettingsOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [settingsBtnPos, setSettingsBtnPos] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const settingsBtnRef = useRef<any>(null);

  // Load saved hidden columns from storage on mount
  useEffect(() => {
    AsyncStorage.getItem("production_stocks_hidden_columns").then((stored) => {
      if (stored) {
        try {
          setHiddenColumns(JSON.parse(stored));
        } catch { }
      }
    });
    AsyncStorage.getItem("production_stocks_column_order").then((stored) => {
      if (stored) {
        try {
          setColumnOrder(JSON.parse(stored));
        } catch { }
      }
    });
  }, []);

  useEffect(() => {
    navigation.setOptions({ title: t("productionStocks") });
  }, [navigation, language, t]);

  // All available headers based on device size
  const getAllHeaders = () => {
    const base = isLargeDevice ? headers : isMediumDevice ? MediumDeviceHeaders : SmallDeviceHeaders;
    return translateHeaders(base, t, productionStockTranslationMap);
  };

  const getOrderedKeys = (baseHeaders: Record<string, Record<string, string | number | boolean>>) => {
    const allKeys = Object.keys(baseHeaders);
    if (columnOrder.length === 0) return allKeys;
    const ordered: string[] = [];
    columnOrder.forEach((k) => { if (allKeys.includes(k)) ordered.push(k); });
    allKeys.forEach((k) => { if (!ordered.includes(k)) ordered.push(k); });
    return ordered;
  };

  const getFilteredHeaders = (baseHeaders: Record<string, Record<string, string | number | boolean>>) => {
    const orderedKeys = getOrderedKeys(baseHeaders);
    const visibleEntries = orderedKeys
      .filter((key) => !hiddenColumns.includes(key))
      .map((key) => [key, baseHeaders[key]] as [string, Record<string, string | number | boolean>]);
    const totalOriginalFlex = Object.values(baseHeaders).reduce((sum, h) => sum + (Number(h.width) || 0), 0);
    const totalVisibleFlex = visibleEntries.reduce((sum, [, h]) => sum + (Number(h.width) || 0), 0);
    const scale = totalVisibleFlex > 0 ? totalOriginalFlex / totalVisibleFlex : 1;

    const result: Record<string, Record<string, string | number | boolean>> = {};
    visibleEntries.forEach(([key, value]) => {
      result[key] = { ...value, width: Number(value.width) * scale };
    });
    return result;
  };

  // Update table headers when hidden columns change or device size changes
  useEffect(() => {
    const allHeaders = getAllHeaders();
    setTableHeaders(getFilteredHeaders(allHeaders));
  }, [hiddenColumns, columnOrder, isLargeDevice, isMediumDevice, language]);

  const toggleColumn = (columnKey: string) => {
    setHiddenColumns((prev) => {
      const updated = prev.includes(columnKey)
        ? prev.filter((k) => k !== columnKey)
        : [...prev, columnKey];
      AsyncStorage.setItem("production_stocks_hidden_columns", JSON.stringify(updated));
      return updated;
    });
  };

  const moveColumn = useCallback((fromKey: string, toKey: string) => {
    const allHeaders = getAllHeaders();
    const currentKeys = getOrderedKeys(allHeaders).filter((k) => k !== "");
    const fromIdx = currentKeys.indexOf(fromKey);
    const toIdx = currentKeys.indexOf(toKey);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const newKeys = [...currentKeys];
    newKeys.splice(fromIdx, 1);
    newKeys.splice(toIdx, 0, fromKey);
    newKeys.push("");
    setColumnOrder(newKeys);
    AsyncStorage.setItem("production_stocks_column_order", JSON.stringify(newKeys));
  }, [columnOrder]);

  const resetColumnOrder = () => {
    setColumnOrder([]);
    AsyncStorage.removeItem("production_stocks_column_order");
  };

  // Drag-and-drop reordering via window mouse events
  const [dragActiveKey, setDragActiveKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const dragRef = useRef<{ activeKey: string | null; overKey: string | null }>({ activeKey: null, overKey: null });

  const startColumnDrag = useCallback((key: string) => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    dragRef.current.activeKey = key;
    setDragActiveKey(key);

    const onMouseMove = (e: MouseEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const row = el && (el as HTMLElement).closest ? (el as HTMLElement).closest("[data-columnkey]") : null;
      const overKey = row ? row.getAttribute("data-columnkey") : null;
      if (overKey !== dragRef.current.overKey) {
        dragRef.current.overKey = overKey;
        setDragOverKey(overKey && overKey !== dragRef.current.activeKey ? overKey : null);
      }
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      (document.body.style as any).userSelect = "";
      const from = dragRef.current.activeKey;
      const to = dragRef.current.overKey;
      if (from && to && from !== to) {
        moveColumn(from, to);
      }
      dragRef.current = { activeKey: null, overKey: null };
      setDragActiveKey(null);
      setDragOverKey(null);
    };

    document.body.style.cursor = "grabbing";
    (document.body.style as any).userSelect = "none";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }, [moveColumn]);

  const displayTableLoader = () => {
    handleTableLoader(true, false, setIsSearched);
  };

  const fadeTableLoader = () => {
    handleTableLoader(false, true, setIsSearched);
  };

  const onHandleSortColumn = (columnKey: string) => {
    const _sortedPmxDetailList: Array<productionStockModel> = sortColumns(
      prodStockPageLevelList,
      columnKey,
      tableHeaders,
      setTableHeaders
    );

    setProdStockPageLevelList(_sortedPmxDetailList);
  };

  const handleSearch = (searchText: string) => {
    displayTableLoader();

    if (searchText.trim() !== "") {
      const searchedList: Array<productionStockModel> = filterBasedOnAnyKey(
        productionStockReport,
        searchText
      );
      setProdStockPageLevelList(searchedList);
    }

    setTimeout(() => {
      fadeTableLoader();
    }, 1000);
  };

  useEffect(() => {
    let isMounted = true;

    // Avoid keep refreshing table when item is selected
    if (isMounted) {
      if (prodStockPageLevelList.length === 0 || isRefresh) {
        displayTableLoader();
        getQldProductionStocks("", tableHeaders);
      }
    }

    const allHeaders = getAllHeaders();
    setTableHeaders(getFilteredHeaders(allHeaders));

    setRefresh(false);

    return () => {
      isMounted = false;
    };
  }, [selected, isRefresh]);

  useEffect(() => {
    setProdStockPageLevelList(productionStockReport);

    setTimeout(() => {
      fadeTableLoader();
    }, 2000);
  }, [productionStockReport]);

  const navigateToOrder = (order: any, action: string) => {
    navigation.navigate("ProductionStockDetailsReportScreen", { order });
  };

  return (
    <ScrollView style={{ flex: 1, backgroundColor: colors.surface }} contentContainerStyle={{ flexGrow: 1 }}>
      <View style={[styles.container, { backgroundColor: colors.surfaceSubtle }]}>
        <View
          style={[
            styles.containerContent,
            {
              width: "100%",
            },
          ]}
        >
          {isProductionStockReportListLoaded ? (
            <View>
              <LabelBannerWithSearchBar
                titleName={`${t("totalItems")}: ${prodStockPageLevelList.length}`}
                handleSearch={handleSearch}
                setRefresh={setRefresh}
                preSearchControls={
                  <RNView>
                    <TouchableOpacity
                      ref={settingsBtnRef}
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        backgroundColor: colors.surfaceSubtle,
                        justifyContent: "center",
                        alignItems: "center",
                        borderWidth: 1,
                        borderColor: columnSettingsOpen ? colors.primary : colors.border,
                      }}
                      onPress={() => {
                        if (settingsBtnRef.current) {
                          settingsBtnRef.current.measureInWindow(
                            (x: number, y: number, width: number, height: number) => {
                              setSettingsBtnPos({ x: x || 0, y: y || 0, width: width || 0, height: height || 0 });
                              setColumnSettingsOpen(!columnSettingsOpen);
                            }
                          );
                        } else {
                          setColumnSettingsOpen(!columnSettingsOpen);
                        }
                      }}
                    >
                      <Feather
                        name="settings"
                        size={15}
                        color={columnSettingsOpen ? colors.primary : colors.textSecondary}
                      />
                    </TouchableOpacity>
                  {columnSettingsOpen && (
                    <RNModal
                      transparent={true}
                      visible={columnSettingsOpen}
                      animationType="fade"
                      onRequestClose={() => setColumnSettingsOpen(false)}
                    >
                      <Pressable
                        style={{
                          flex: 1,
                          backgroundColor: "rgba(0,0,0,0.15)",
                        }}
                        onPress={() => setColumnSettingsOpen(false)}
                      >
                        <Pressable
                          style={{
                            position: "absolute",
                            top: settingsBtnPos.y + settingsBtnPos.height + 6,
                            left: settingsBtnPos.x - 180 + 40,
                            backgroundColor: colors.surface,
                            borderRadius: 12,
                            borderWidth: 1,
                            borderColor: colors.border,
                            paddingVertical: 10,
                            minWidth: 220,
                            shadowColor: "#000",
                            shadowOffset: { width: 0, height: 8 },
                            shadowOpacity: 0.15,
                            shadowRadius: 20,
                            elevation: 10,
                          }}
                          onPress={(e) => e.stopPropagation()}
                        >
                          <Text
                            style={{
                              fontSize: 12,
                              fontWeight: "700",
                              color: colors.textSecondary,
                              paddingHorizontal: 16,
                              paddingBottom: 8,
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            {t("showHideColumns")}
                          </Text>
                          <View style={{ height: 1, backgroundColor: colors.borderSubtle, marginBottom: 4 }} />
                          {/* Select All */}
                          <TouchableOpacity
                            style={{
                              flexDirection: "row",
                              alignItems: "center",
                              paddingVertical: 8,
                              paddingHorizontal: 16,
                            }}
                            onPress={() => {
                              const allKeys = Object.keys(getAllHeaders()).filter((k) => k !== "");
                              if (hiddenColumns.length === 0) {
                                const updated = [...allKeys];
                                setHiddenColumns(updated);
                                AsyncStorage.setItem("production_stocks_hidden_columns", JSON.stringify(updated));
                              } else {
                                setHiddenColumns([]);
                                AsyncStorage.setItem("production_stocks_hidden_columns", JSON.stringify([]));
                              }
                            }}
                          >
                            <View
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: 4,
                                borderWidth: 1.5,
                                borderColor: hiddenColumns.length === 0 ? colors.primary : colors.textMuted,
                                backgroundColor: hiddenColumns.length === 0 ? colors.primary : colors.surface,
                                justifyContent: "center",
                                alignItems: "center",
                                marginRight: 10,
                              }}
                            >
                              {hiddenColumns.length === 0 && (
                                <Feather name="check" size={13} color="#fff" />
                              )}
                            </View>
                            <Text
                              style={{
                                fontSize: 14,
                                color: colors.text,
                                fontWeight: "700",
                              }}
                            >
                              {t("selectAll")}
                            </Text>
                          </TouchableOpacity>
                          <View style={{ height: 1, backgroundColor: colors.borderSubtle, marginBottom: 2 }} />
                          <ScrollView style={{ maxHeight: 300 }} showsVerticalScrollIndicator={true}>
                            {getOrderedKeys(getAllHeaders()).map((key) => {
                              if (key === "") return null;
                              const value = getAllHeaders()[key];
                              if (!value) return null;
                              const isVisible = !hiddenColumns.includes(key);
                              const isDragging = dragActiveKey === key;
                              const isDragOver = dragOverKey === key;
                              return (
                                <View
                                  key={key}
                                  {...{ dataSet: { columnkey: key } } as any}
                                  style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    paddingVertical: 8,
                                    paddingHorizontal: 16,
                                    opacity: isDragging ? 0.35 : 1,
                                    backgroundColor: isDragOver ? colors.primaryBg : "transparent",
                                    borderTopWidth: isDragOver ? 2 : 0,
                                    borderTopColor: colors.primary,
                                  }}
                                >
                                  <Pressable
                                    onPressIn={() => startColumnDrag(key)}
                                    style={{ padding: 4, marginRight: 6, cursor: "grab" } as any}
                                  >
                                    <Feather name="menu" size={14} color={colors.textMuted} />
                                  </Pressable>
                                  <TouchableOpacity
                                    style={{ flexDirection: "row", alignItems: "center", flex: 1 }}
                                    onPress={() => toggleColumn(key)}
                                  >
                                    <View
                                      style={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: 4,
                                        borderWidth: 1.5,
                                        borderColor: isVisible ? colors.primary : colors.textMuted,
                                        backgroundColor: isVisible ? colors.primary : colors.surface,
                                        justifyContent: "center",
                                        alignItems: "center",
                                        marginRight: 10,
                                      }}
                                    >
                                      {isVisible && (
                                        <Feather name="check" size={13} color="#fff" />
                                      )}
                                    </View>
                                    <Text
                                      style={{
                                        fontSize: 14,
                                        color: isVisible ? colors.text : colors.textMuted,
                                        fontWeight: isVisible ? "600" : "400",
                                      }}
                                    >
                                      {(value as any).name}
                                    </Text>
                                  </TouchableOpacity>
                                </View>
                              );
                            })}
                          </ScrollView>
                          {columnOrder.length > 0 && (
                            <>
                              <View style={{ height: 1, backgroundColor: colors.borderSubtle, marginTop: 4 }} />
                              <TouchableOpacity
                                style={{
                                  flexDirection: "row",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  paddingVertical: 10,
                                  paddingHorizontal: 16,
                                }}
                                onPress={resetColumnOrder}
                              >
                                <Feather name="rotate-ccw" size={13} color={colors.textSecondary} />
                                <Text style={{ fontSize: 13, color: colors.textSecondary, fontWeight: "600", marginLeft: 6 }}>
                                  {t("resetOrder")}
                                </Text>
                              </TouchableOpacity>
                            </>
                          )}
                        </Pressable>
                      </Pressable>
                    </RNModal>
                  )}
                  </RNView>
                }
              />
              <DataTableWithPagination
                headers={tableHeaders}
                rows={prodStockPageLevelList}
                itemsPerPage={100}
                useModal={false}
                isDetail={true}
                setModalVisible={setModalVisible}
                setSelected={setSelected}
                isDataLoading={isSearched}
                handleRowClick={() => { }}
                handleDeleteRow={() => { }}
                handleSortColumn={onHandleSortColumn}
                operationActions={navigateToOrder}
              />

            </View>
          ) : (
            <View>
              <Spinner
                size="small"
                color="#74BBFF"
                styles={{ flexDirection: "row", margin: "20%" }}
              />
            </View>
          )}
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
  },
  modalBodyContainer: {
    width: "100%",
    alignSelf: "center",
    backgroundColor: "black",
  },
  modalBodyContainerContent: {
    flexDirection: "row",
    width: "100%",
    alignSelf: "center",
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
  },
});
